import * as React from 'react'
import cn from 'classnames'
import { Icon } from 'src/design-system'
import {
  ChevronLeft,
  Options2Outline,
  PaperPlaneOutline,
} from '@easy-eva-icons/react'
import { Loader } from 'src/design-system'
import styles from './Chat.module.scss'
import Markdown from 'react-markdown'
import { CaretLeft } from '@phosphor-icons/react'

export type Message = {
  id?: string
  role: 'user' | 'assistant'
  content: string
}

export type ChatHandle = {
  scrollToMessage: (messageId: string) => void
}

type ChatProps = {
  id: number | null
  loading: boolean
  messages: Message[]
  onAdjustSkills: () => void
  onBack: () => void
  onSubmit: (msg: string) => void
  view: 'full' | 'side'
}

export const Chat = React.forwardRef<ChatHandle, ChatProps>(
  ({ id, loading, messages, onAdjustSkills, onBack, onSubmit, view }, ref) => {
    const [prompt, setPrompt] = React.useState('')
    const [validationMessage, setValidationMessage] = React.useState('')
    const messageRefs = React.useRef<Map<string, HTMLDivElement>>(new Map())
    const containerRef = React.useRef<HTMLDivElement | null>(null)

    React.useEffect(() => {
      if (containerRef.current == null) return

      requestAnimationFrame(() => {
        if (containerRef.current == null) return
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef.current, id])

    React.useEffect(() => {
      if (containerRef.current == null) return

      requestAnimationFrame(() => {
        if (containerRef.current == null) return
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    }, [messages, loading])

    React.useImperativeHandle(ref, () => ({
      scrollToMessage: (messageId: string) => {
        const element = messageRefs.current.get(messageId)
        element?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      },
    }))

    const handleSubmit = (event: React.FormEvent) => {
      event.preventDefault()
      const trimmedPrompt = prompt.trim()
      if (trimmedPrompt.length === 0) {
        setValidationMessage('Please enter a prompt before submitting.')
        return
      }

      onSubmit(trimmedPrompt)
      setPrompt('')
      setValidationMessage('')
    }

    const fillPrompt = (prompt: string) => {
      setPrompt(prompt)
      setValidationMessage('')
    }

    const sendOnEnter: React.KeyboardEventHandler<HTMLTextAreaElement> = (
      e,
    ) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        e.currentTarget.closest('form')!.requestSubmit()
      }
    }

    return (
      <div className={cn(styles.card, view === 'side' && styles.sideView)}>
        {view === 'side' && (
          <button className={styles.backButton} onClick={onBack}>
            <Icon size="sm" style={{ color: 'currentcolor' }}>
              <ChevronLeft />
            </Icon>
            Chat
          </button>
        )}
        {messages.length === 0 && (
          <header className={styles.header}>
            <h1 className={styles.title}>
              Hello! I&apos;m your Workforce Intelligence
              <br /> AI assistant.
            </h1>
          </header>
        )}

        <section className={styles.chatSection}>
          {messages.length > 0 && (
            <div
              className={cn(
                styles.messagesContainer,
                view === 'side' && styles.side,
              )}
              ref={containerRef}
            >
              <div className={styles.messagesGradientContainer}>
                {messages.map((message) => (
                  <div
                    className={cn(
                      styles.messageBubble,
                      message.role === 'user' ? styles.user : styles.assistant,
                    )}
                    key={message.id}
                    ref={(el) => {
                      if (el && message.id) {
                        messageRefs.current.set(message.id, el)
                      }
                    }}
                  >
                    {message.role === 'assistant' ? (
                      <Markdown>{message.content}</Markdown>
                    ) : (
                      message.content
                    )}
                  </div>
                ))}
                {loading && <Loader />}
              </div>
            </div>
          )}

          <form className={styles.inputContainer} onSubmit={handleSubmit}>
            <div className={styles.growableArea} data-replicated-value={prompt}>
              <textarea
                className={styles.textarea}
                aria-describedby={
                  validationMessage.length > 0
                    ? 'wfi-prompt-validation-message'
                    : undefined
                }
                aria-invalid={validationMessage.length > 0}
                disabled={loading}
                key="prompt-textarea"
                onChange={(event) => {
                  setPrompt(event.target.value)
                  if (validationMessage.length > 0) setValidationMessage('')
                }}
                onKeyUp={sendOnEnter}
                placeholder="Ask anything"
                value={prompt}
              />
            </div>
            {validationMessage.length > 0 && (
              <p
                className={styles.validationMessage}
                id="wfi-prompt-validation-message"
                role="alert"
              >
                {validationMessage}
              </p>
            )}

            <div className={styles.inputActions}>
              <button
                type="button"
                aria-label="Adjust skills"
                className={styles.adjustButton}
                onClick={onAdjustSkills}
              >
                <span aria-hidden="true">
                  <Icon size="sm" style={{ color: 'currentcolor' }}>
                    <Options2Outline />
                  </Icon>
                </span>
              </button>

              <button
                type="submit"
                aria-label="Submit prompt"
                className={styles.sendButton}
              >
                <span aria-hidden="true">
                  <Icon size="sm" style={{ color: 'currentcolor' }}>
                    <PaperPlaneOutline />
                  </Icon>
                </span>
              </button>
            </div>
          </form>

          {view === 'full' && messages.length === 0 && (
            <PromptExamples onSelect={fillPrompt} />
          )}
        </section>
        {view === 'full' && (
          <p className={styles.disclaimer}>
            AI-generated insights based on available data. Review before
            implementation.
          </p>
        )}
      </div>
    )
  },
)

Chat.displayName = 'Chat'

type PromptExamplesProps = {
  onSelect: (prompt: string) => void
}

const PROMPTS: [string, string[]][] = [
  [
    'Workforce Optimization & Cost Efficiency',
    [
      'Show me teams with skill overlap where we could consolidate roles without losing capability',
      "What's our current span of control across managers, and where are we over or under-staffed?",
      'Identify positions that have been vacant for 90+ days and calculate the productivity cost',
      'Which roles could be transitioned to remote work to reduce our real estate footprint?',
    ],
  ],
  [
    'Skill Development & Upskilling Strategy',
    [
      "What are the most in-demand skills in our industry that we're currently lacking internally?",
      'Which employees have adjacent skills that could be developed to fill upcoming role requirements?',
      "Show me teams where emerging technology skills are concentrated versus where they're needed most",
      "What's the skills gap between our current workforce and our strategic initiatives for next year?",
    ],
  ],
  [
    'Critical Talent Retention & Risk Management',
    [
      'Who are our top performers with no promotion in the last 3 years and high flight risk indicators?',
      'Identify single points of failure—employees with unique knowledge and no clear backup',
      'Show me retention rates by manager to identify teams with concerning turnover patterns',
      'Show me compensation gaps where our top talent is paid below market rate for their role and location',
    ],
  ],
  [
    'Leadership Pipeline & Succession Planning ',
    [
      'For each executive role, who are the internal candidates and what gaps exist in their readiness?',
      "Map out employees with high leadership potential who aren't currently in management tracks",
      "What's our bench strength for director-level and above roles across different business units?",
      'Identify critical roles where we have no viable internal successor',
    ],
  ],
]

const PromptExamples: React.VFC<PromptExamplesProps> = ({ onSelect }) => {
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null)

  return (
    <section className={styles.suggestions}>
      {selectedIndex == null && (
        <>
          <h2>Select a category to see more prompt examples</h2>
          <ul>
            {PROMPTS.map(([category], i) => (
              <li key={category}>
                <button onClick={() => setSelectedIndex(i)}>{category}</button>
              </li>
            ))}
          </ul>
        </>
      )}
      {selectedIndex != null && (
        <>
          <h2>
            <button
              aria-label="Back"
              className={styles.promptExampleTitle}
              onClick={() => setSelectedIndex(null)}
            >
              <CaretLeft size="16px" />
              {PROMPTS[selectedIndex][0]}
            </button>
          </h2>
          <ul>
            {PROMPTS[selectedIndex][1].map((prompt) => (
              <li key={prompt}>
                <button onClick={() => onSelect(prompt)}>{prompt}</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
